import React from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, Package, ScanLine, ArrowRight, Check, BookOpen } from "lucide-react";

/**
 * 3-step onboarding wizard for brand-new admin accounts.
 * Renders once per user (per browser): completion is tracked in
 *   localStorage[`vv_onboarding_done:${user_id}`].
 *
 * Mount it in Dashboard with a guard on user.role === "admin" AND
 * summary.total_products === 0 so we don't pester existing users or staff.
 */
export default function OnboardingWizard({ user, onClose }) {
  const [step, setStep] = React.useState(0);
  const close = () => {
    try { localStorage.setItem(`vv_onboarding_done:${user.user_id}`, "1"); } catch { /* ignore */ }
    onClose?.();
  };

  const steps = [
    {
      icon: Sparkles,
      eyebrow: `Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}.`,
      title: "Let's get your booth ready.",
      body: (
        <>
          <p className="font-mono text-sm leading-relaxed text-[#333]">
            Vendor Vault is built around three things you'll do every show: <strong>track inventory</strong>,
            <strong> scan to sell</strong>, and <strong>see what to restock</strong>. Three quick stops
            and you'll be running.
          </p>
          <p className="font-mono text-sm leading-relaxed text-[#333] mt-3">
            You're on a <strong>14-day Pro trial</strong> — all integrations and team invites unlocked. No
            credit card needed yet.
          </p>
          <ul className="mt-5 space-y-2 font-mono text-xs">
            <li className="flex items-center gap-2"><Check size={14} className="text-[#4A7C59]"/> Up next: add your first product</li>
            <li className="flex items-center gap-2"><Check size={14} className="text-[#4A7C59]"/> Then: print barcodes and scan</li>
            <li className="flex items-center gap-2"><Check size={14} className="text-[#4A7C59]"/> You can re-open this from /help anytime</li>
          </ul>
        </>
      ),
      primary: { label: "Let's go →", onClick: () => setStep(1) },
    },
    {
      icon: Package,
      eyebrow: "Step 1 of 2",
      title: "Add your first product.",
      body: (
        <>
          <p className="font-mono text-sm leading-relaxed text-[#333]">
            A <strong>product</strong> is anything you sell (a tee, a sticker pack, a print). Each product can
            have multiple <strong>variants</strong> (sizes, colors, editions) — and each variant gets its own
            auto-generated SKU and Code128 barcode.
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/products/new"
              onClick={close}
              className="brutal-card-sm bg-white p-4 hover:bg-[#F4F2EC] block"
              data-testid="onboarding-add-one"
            >
              <div className="flex items-center gap-2 font-heading text-base font-black uppercase">
                <Package size={16}/> Add one product
              </div>
              <p className="font-mono text-xs text-[#4A4A4A] mt-2">Best if you've got a few items in mind. Takes 30 seconds.</p>
            </Link>
            <Link
              to="/products"
              onClick={close}
              className="brutal-card-sm bg-white p-4 hover:bg-[#F4F2EC] block"
              data-testid="onboarding-import-csv"
            >
              <div className="flex items-center gap-2 font-heading text-base font-black uppercase">
                <BookOpen size={16}/> Import a CSV
              </div>
              <p className="font-mono text-xs text-[#4A4A4A] mt-2">Already have a catalog? Bulk import on the Products page.</p>
            </Link>
          </div>
          <p className="font-mono text-[11px] text-[#4A4A4A] mt-4">
            Tip: don't sweat barcodes — they print on a regular printer onto sticker paper or shipping tags.
          </p>
        </>
      ),
      primary: { label: "Next →", onClick: () => setStep(2) },
      secondary: { label: "Back", onClick: () => setStep(0) },
    },
    {
      icon: ScanLine,
      eyebrow: "Step 2 of 2",
      title: "Scan and sell.",
      body: (
        <>
          <p className="font-mono text-sm leading-relaxed text-[#333]">
            On show day, open <code>/scanner</code> on your phone (install Vendor Vault as a PWA first — Add
            to Home Screen on iOS, Install App on Android). Point the webcam at a tag, tap{" "}
            <strong>Log sale</strong>, done.
          </p>
          <ul className="mt-5 space-y-2 font-mono text-xs leading-relaxed">
            <li className="flex items-start gap-2"><Check size={14} className="text-[#4A7C59] mt-[3px]"/> Persistent cart per event — survives backgrounding and tab kills.</li>
            <li className="flex items-start gap-2"><Check size={14} className="text-[#4A7C59] mt-[3px]"/> Charge with Square (Pro): deep-link into Square POS with the total pre-filled.</li>
            <li className="flex items-start gap-2"><Check size={14} className="text-[#4A7C59] mt-[3px]"/> Offline-first: sales queue locally and auto-sync when you're back online.</li>
          </ul>
          <div className="mt-5 brutal-card-sm bg-[#F4F2EC] p-4">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">After the show</div>
            <p className="font-mono text-xs mt-2">
              Every event page has a sale-by-sale ledger, a by-day chart, and per-seller payout breakdowns.
              You'll know exactly what to restock before the next gate opens.
            </p>
          </div>
        </>
      ),
      primary: { label: "Try the scanner →", onClick: () => { window.location.assign("/scanner"); close(); } },
      secondary: { label: "Back", onClick: () => setStep(1) },
    },
  ];

  const current = steps[step];
  const Icon = current.icon;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center px-4 sm:px-6 py-6"
      data-testid="onboarding-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="brutal-card bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 p-2 hover:bg-[#F4F2EC] border-2 border-transparent hover:border-black"
          aria-label="Close"
          data-testid="onboarding-close"
        >
          <X size={16}/>
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#C2410C] border-2 border-black flex items-center justify-center">
              <Icon className="text-white" size={20}/>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">{current.eyebrow}</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase leading-tight mt-0.5">{current.title}</h2>
            </div>
          </div>

          <div className="mt-6">{current.body}</div>

          {/* Step dots */}
          <div className="mt-7 flex items-center justify-center gap-2" aria-hidden>
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-2 transition-all ${i === step ? "w-8 bg-[#C2410C]" : "w-2 bg-[#4A4A4A]/30"}`}
              />
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <div>
              {current.secondary ? (
                <button
                  type="button"
                  onClick={current.secondary.onClick}
                  className="brutal-btn brutal-btn-secondary"
                  data-testid="onboarding-back"
                >
                  {current.secondary.label}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={close}
                  className="font-mono text-xs underline text-[#4A4A4A] hover:text-black"
                  data-testid="onboarding-skip"
                >
                  Skip for now
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={current.primary.onClick}
              className="brutal-btn inline-flex items-center gap-2"
              data-testid="onboarding-next"
            >
              {current.primary.label} {step === 2 ? null : <ArrowRight size={14}/>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
